import { Alert, Button, Snackbar, Box } from '@mui/material'
import { usePageNavigation } from 'hooks/useNavigation'
import AddIcon from '@mui/icons-material/Add'
import React from 'react'
import ToolBox from './ToolBox'
import {
  EditInstagramToolObject,
  ToolStatus,
  useChangeToolStatusMutation,
  useDeleteInstagramToolMutation,
  useEditInstagramToolMutation,
  useGetInstagramToolsQuery,
} from 'graphql/generated/graphql'
import Spinner from 'components/Spinner/Spinner'
import { Sparkles, Zap, Plus } from 'lucide-react'

export default function InstagramAutomation(): JSX.Element {
  const { navigateToCreateAutomation } = usePageNavigation()
  const { data, loading, refetch } = useGetInstagramToolsQuery({
    fetchPolicy: 'network-only',
  })
  const [statusSnackbar, setStatusSnackbar] = React.useState(false)
  const [deleteSnackbar, setDeleteSnackbar] = React.useState(false)
  const [editSnackBar, setEditSnackBar] = React.useState(false)
  const [errorSnackBar, setErrorSnackBar] = React.useState(false)
  const [changeStatus] = useChangeToolStatusMutation()
  const [deleteTool] = useDeleteInstagramToolMutation()
  const [editInstagramTool] = useEditInstagramToolMutation()

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Spinner />
      </Box>
    )
  }

  // CHANGE STATUS
  const changeStatusFunc = async (
    status: boolean,
    id: number
  ): Promise<void> => {
    if (status) {
      const success = await changeStatus({
        variables: {
          status: ToolStatus.Active,
          id,
        },
      })
      if (success) {
        setStatusSnackbar(true)
      } else {
        setErrorSnackBar(true)
      }
    } else {
      const success = await changeStatus({
        variables: {
          status: ToolStatus.Inactive,
          id,
        },
      })
      if (success) {
        setStatusSnackbar(true)
      } else {
        setErrorSnackBar(true)
      }
    }
  }

  // DELETE TOOL
  const deleteToolFunc = async (id: number): Promise<void> => {
    const deleted = await deleteTool({
      variables: {
        id,
      },
    })
    await refetch()
    if (deleted) {
      setDeleteSnackbar(true)
    } else {
      setErrorSnackBar(true)
    }
  }

  // EDIT TOOL
  const editToolFunc = async (
    id: number,
    data: EditInstagramToolObject
  ): Promise<void> => {
    const edited = await editInstagramTool({
      variables: {
        id,
        data,
      },
    })
    await refetch()
    if (edited) {
      setEditSnackBar(true)
    } else {
      setErrorSnackBar(true)
    }
  }

  const activeTools = data?.getInstagramTools.filter(
    (tool) => tool?.status === 'Active'
  ).length ?? 0

  const totalRuns = data?.getInstagramTools.reduce(
    (acc, tool) => acc + (tool?.triggered ?? 0),
    0
  ) ?? 0

  return (
    <Box
      sx={{
        padding: '24px',
        minHeight: '100vh',
      }}
    >
      {/* Header Section with Gradient */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '32px',
          padding: '32px',
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(12px)',
          borderRadius: '20px',
          border: '1px solid rgba(168, 85, 247, 0.2)',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(168, 85, 247, 0.4)',
              }}
            >
              <Zap size={28} color="#ffffff" />
            </Box>
            <Box>
              <h2
                style={{
                  margin: 0,
                  fontSize: '36px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Automations
              </h2>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  marginTop: '8px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#10b981',
                      boxShadow: '0 0 8px #10b981',
                    }}
                  />
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                    {activeTools} active
                  </Box>
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>•</Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                  {totalRuns} total runs
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          sx={{
            background: 'linear-gradient(135deg, #a855f7, #ec4899)',
            color: '#ffffff',
            borderRadius: '12px',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea, #db2777)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(168, 85, 247, 0.5)',
            },
          }}
          onClick={() => navigateToCreateAutomation('Instagram')} 
        >
          Create Automation
        </Button>
      </Box>

      {/* Empty State or Tools Grid */}
      {data?.getInstagramTools.length === 0 ? (
        <Box
          sx={{
            padding: '64px 32px',
            background: 'rgba(30, 41, 59, 0.3)',
            backdropFilter: 'blur(12px)',
            borderRadius: '20px',
            border: '2px dashed rgba(168, 85, 247, 0.3)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <Sparkles size={40} color="#a855f7" />
          </Box>
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '24px',
              fontWeight: 600,
              color: '#ffffff',
            }}
          >
            No Automations Yet
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.6)',
              maxWidth: '480px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Get started by creating your first automation tool. Click the &apos;Create Automation&apos; button to begin automating your social media engagement!
          </p>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: '24px',
          }}
        >
          {data?.getInstagramTools.map((item) => {
            return (
              <ToolBox
                key={item?.id}
                title={item?.title ?? 'Instagram automation tool'}
                runs={item?.triggered ?? 0}
                active={item?.status === 'Active'}
                keyword={item?.keyword ?? ''}
                shouldReply={item?.shouldReply ?? false}
                commentReply={item?.commentReply ?? []}
                message={item?.message ?? []}
                statusMutation={changeStatusFunc}
                deleteMutation={deleteToolFunc}
                editMutation={editToolFunc}
                id={item?.id ?? 0}
              />
            )
          })}
        </Box>
      )}

      {/* Snackbars */}
      <Snackbar
        open={statusSnackbar}
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          setStatusSnackbar(false)
        }}
      >
        <Alert
          onClose={() => {
            setStatusSnackbar(false)
          }}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            },
          }}
        >
          Tool status updated successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={deleteSnackbar}
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          setDeleteSnackbar(false)
        }}
      >
        <Alert
          onClose={() => {
            setDeleteSnackbar(false)
          }}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          }}
        >
          Deleted tool successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={editSnackBar}
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          setEditSnackBar(false)
        }}
      >
        <Alert
          onClose={() => {
            setEditSnackBar(false)
          }}
          severity="info"
          variant="filled"
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          }}
        >
          Edited tool successfully!
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorSnackBar}
        autoHideDuration={2000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={() => {
          setErrorSnackBar(false)
        }}
      >
        <Alert
          onClose={() => {
            setErrorSnackBar(false)
          }}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          }}
        >
          Something went wrong, please try again later.
        </Alert>
      </Snackbar>
    </Box>
  )
}